<template>
  <!-- 流程协作群 -->
  <section class="workflow-chat" :class="{ embedded: conversationOnly, 'compact-header': compactHeader, 'is-mobile': mobile }" :aria-label="$t('workflowChat.title')">
    <div class="discussion">
      <!-- 重试 -->
      <div v-if="error" class="error" role="alert">
        {{ error }}
        <el-button type="text" @click="refresh">{{
          $t("agentServiceUI.retry")
        }}</el-button>
      </div>
      <template v-if="active">
        <transition name="group-info"><header v-show="!compactHeader || infoOpen" class="heading" :class="{ 'info-popover': compactHeader }" @keydown.esc.stop="infoOpen = false">
          <button v-if="mobile && compactHeader" type="button" class="group-info-back" :aria-label="$t('chatWorkbench.back')" @click="infoOpen = false"><ChatNavigationIcon name="back" /></button>
          <strong :title="conversationTitle">{{ conversationTitle }}</strong>
          <span>{{ stateText(active.group) }}</span>
          <!-- {time}转为只读归档 -->
          <small v-if="active.group.archiveAt">{{
            $t("workflowChat.archiveAt", {
              time: dateTime(active.group.archiveAt),
            })
          }}</small>
          <details class="group-settings">
            <summary>{{ $t("workflowChat.groupSettings", { count: active.members.length }) }}</summary>
            <p class="manager">{{ $t("workflowChat.agentManager") }}</p>
            <el-input v-model="memberSearch" size="small" :placeholder="$t('messageCenter.searchContacts')" />
            <div v-for="member in filteredMembers" :key="member.userId" class="member">
              <span>{{ member.displayName }}（{{ roleText(member.roles) }}）</span>
              <button type="button" :disabled="String(member.userId) === currentUserId" @click="privateChat(member)">{{ $t("workflowChat.privateChat") }}</button>
            </div>
          </details>
        <workflow-chat-agent-panel v-if="!mobile" inline :active="visible" :group="active.group" :current-user-id="currentUserId" />
        </header></transition>
        <workflow-chat-agent-panel v-if="mobile" mobile inline :active="visible" :can-request="active.canSend" :group="active.group" :current-user-id="currentUserId" />
        <div v-if="highlightedSequence || citationLoading || citationError" class="citation-toolbar" role="status">
          <span>{{ citationError || (citationLoading ? $t('wallet.loadingShort') : $t('workflowChat.viewingReference', { number: highlightedSequence })) }}</span>
          <button v-if="citationContext && citationHasNewer" type="button" :disabled="citationLoading" @click="loadCitationPage('newer')">{{ $t('workflowChat.laterMessages') }}</button>
          <button type="button" @click="returnFromCitation">{{ $t('workflowChat.returnToChat') }}</button>
        </div>
        <!-- 流程聊天记录 -->
        <div
          v-stable-chat-width
          ref="messages"
          class="messages"
          role="log"
          :aria-label="$t('workflowChat.history')"
          @scroll="onScroll"
        >
          <div data-chat-width-canvas>
          <!-- 加载中 -->
          <!-- 查看更早记录 -->
          <el-button
            v-if="citationContext ? citationContext.hasOlder : hasOlder"
            type="text"
            :disabled="loadingOlder || citationLoading"
            @click="loadOlder"
            >{{
              loadingOlder
                ? $t("wallet.loadingShort")
                : $t("workflowChat.earlierMessages")
            }}</el-button
          >
          <article
            v-for="message in displayedMessages"
            :key="message.sequence" :data-chat-message="message.sequence"
            class="message"
            :id="`workflow-message-${_uid}-${message.sequence}`"
            :data-workflow-sequence="String(message.sequence)"
            tabindex="-1"
            :class="{ mine: String(message.senderUserId) === currentUserId, 'is-cited': String(message.sequence) === String(highlightedSequence) }"
          >
            <div class="author">
              {{ message.senderName }} · {{ dateTime(message.createdAt) }}
            </div>
            <small v-if="mentioned(message)" class="mention-label">{{ $t("workflowChat.mentionedMe") }}</small>
            <div class="body" v-html="renderMessage(message)" @click="onCitationClick" />
          </article>
          <!-- 暂无讨论消息 -->
          <p v-if="!displayedMessages.length && !loading" class="empty">
            {{ $t("workflowChat.noMessages") }}
          </p>
          </div>
        </div>
        <!-- 查看新消息 -->
        <el-button
          v-if="newMessages"
          class="new-messages"
          size="mini"
          @click="scrollToLatest"
          >{{ $t("workflowChat.newMessages") }}</el-button
        >
        <div v-if="active.canSend && mentionOpen" ref="mentionPicker" class="mention-picker" role="listbox" :aria-label="$t('workflowChat.mention')">
          <button v-for="(member, index) in mentionOptions" :key="member.userId" type="button" role="option" :aria-selected="index === mentionIndex" :class="{ selected: index === mentionIndex }" @click="insertMention(member)">{{ member.displayName }}</button>
        </div>
        <AgentServiceComposer :enter-to-send="!mobile" :mobile="mobile" v-if="active.canSend" ref="composer" v-model="draftInput" :show-controls="false" :disabled="!active.canSend" :sending="sending" :can-submit="Boolean(draft.trim())" :placeholder="$t(mobile ? 'messageCenter.enterMessage' : 'workflowChat.messagePlaceholder')" @input-position="updateMentionQuery" @keydown="mentionKeydown" @send="send" />
        <!-- 此轮讨论已归档，可查看历史记录。 -->
        <p v-else class="readonly">{{ $t("workflowChat.archivedHint") }}</p>
      </template>
      <p v-else class="empty">{{ $t("agentServiceUI.connecting") }}</p>
    </div>
  </section>
</template>

<script>
import { renderWorkflowChatMessage } from "@/services/workflow-chat-citations";
import stableChatWidth from "@/services/chat-stable-width";
import { workflowConversationTitle } from "@/services/workflow-chat-inbox";
import { WorkflowChatRealtimeClient } from "@/services/workflow-chat-realtime";
import WorkflowChatAgentPanel from "./WorkflowChatAgentPanel.vue";
import AgentServiceComposer from "@/components/agent-service/AgentServiceComposer.vue";
import ChatNavigationIcon from '@/components/chat-workbench/ChatNavigationIcon.vue';
import { workflowMentionQuery, insertWorkflowMention, reconcileWorkflowMentions, workflowMentionPayload } from "@/services/workflow-chat-mentions";
import * as api from "@/api/workflowChat";
import {
  workflowScopeKey,
  workflowScopeName,
  workflowMessageId,
  mergeWorkflowMessages,
} from "@/services/workflow-chat";
import { resolveAgentServiceCurrentUserId } from "@/services/agent-service-state";

export default {
  name: "WorkflowChatWorkspace",
  directives: { stableChatWidth },
  components: { WorkflowChatAgentPanel, AgentServiceComposer, ChatNavigationIcon },
  props: {
    mobile: { type: Boolean, default: false },
    options: { type: Array, default: () => [] },
    preferredReviewType: { type: Number, default: 0 },
    conversationOnly: { type: Boolean, default: true },
    groupId: { type: String, default: "" },
    visible: { type: Boolean, default: true },
    compactHeader: { type: Boolean, default: false },
  },
  data() {
    return {
      infoOpen: false,
      highlightedSequence: null, citationContext: null, citationLoading: false, citationError: "", citationVersion: 0, citationOriginScroll: null,
      groupAgentReady: false,
      features: { structuredMentions: false, agentMentions: false }, mentionTokens: [], draftMentions: {},
      memberSearch: "", mentionOpen: false, mentionQuery: "", mentionRange: null, mentionIndex: 0,
      realtime: null, realtimeState: "CONNECTING", readPositions: {},
      viewPositions: {},
      groups: [],
      archived: false,
      hasMoreGroups: false,
      listOffset: 0,
      listEpoch: 0,
      active: null,
      preview: null,
      selectedScope: "",
      messages: [],
      hasOlder: false,
      draft: "",
      drafts: {},
      opening: "",
      openingAttempt: null,
      sendAttempt: null,
      error: "",
      loading: false,
      loadingOlder: false,
      sending: false,
      epoch: 0,
      polling: false,
      destroyed: false,
      newMessages: false,
    };
  },
  computed: {
    displayedMessages() { return this.citationContext?.items || this.messages; },
    citationHasNewer() { return Boolean(this.citationContext?.items.length && this.active && this.citationContext.items[this.citationContext.items.length - 1].sequence < this.active.group.lastSequence); },
    conversationTitle() { if (!this.active) return ""; return workflowConversationTitle(this.active, (key, params) => this.$t(key, params)); },
    draftInput: { get() { return this.draft; }, set(value) { this.replaceDraft(value); } },
    filteredMembers() {
      const query = this.memberSearch.trim().toLowerCase();
      return (this.active?.members || []).filter(member => member.displayName.toLowerCase().includes(query));
    },
    mentionOptions() {
      const all = { userId: "all", displayName: this.$t("workflowChat.allMembers") };
      const agent = { userId: "agent", displayName: this.$t("workflowChat.agentName") };
      return [all, ...(this.features.agentMentions && this.groupAgentReady ? [agent] : []), ...(this.active?.members || [])].filter(member => member.displayName.toLowerCase().includes(this.mentionQuery.toLowerCase())).slice(0, 50);
    },
    currentUserId() {
      return String(
        resolveAgentServiceCurrentUserId(this.$store.state.user || {}) || ""
      );
    },
  },
  watch: {
    conversationTitle(value) { if (this.active) this.$emit("title-change", value); },
    infoOpen(value) { this.$emit('info-change', value); },
    visible(value) {
      if (!value) { this.infoOpen = false; this.closeMentions(); this.captureViewPosition(); }
      else this.$nextTick(() => { this.restoreViewPosition(); this.markVisibleRead(); });
    },
    groupId(id) { this.infoOpen = false; if (this.conversationOnly && id) this.selectGroup(id); },
  },
  async mounted() {
    document.addEventListener("click", this.onMentionOutsideClick, true);
    try { this.features = await api.workflowChatFeatures(); } catch { /* 兼容旧服务，只保留文字提及。 */ }
    if (this.destroyed) return;
    document.addEventListener("visibilitychange", this.onVisibility);
    if (this.conversationOnly) await this.selectGroup(this.groupId);
    else await this.loadGroups();
    if (this.destroyed) return;
    const option =
      this.options.find(
        (entry) => entry.scope.reviewType === this.preferredReviewType
      ) || this.options[0];
    if (!this.conversationOnly && option) {
      this.selectedScope = this.scopeKey(option.scope);
      await this.selectScope();
    }
    this.realtime = new WorkflowChatRealtimeClient({
      token: String(this.$store.state.user?.userInfo?.token || ""),
      onState: (state) => { this.realtimeState = state; },
      onEvent: event => {
        if (["resource.revoked", "resource.deleted"].includes(event.eventType)) this.removeResource(event.resourceId);
      },
      onRefresh: () => document.hidden ? Promise.resolve() : this.tick(),
    });
    this.realtime.connect();
  },
  beforeDestroy() {
    this.realtime?.close();
    this.destroyed = true;
    this.epoch++;
    this.listEpoch++;
    document.removeEventListener("visibilitychange", this.onVisibility);
    document.removeEventListener("click", this.onMentionOutsideClick, true);
  },
  methods: {
    renderMessage(message) { return renderWorkflowChatMessage(message, `workflow-message-${this._uid}`); },
    /** 链接只在当前群导航；拦截默认哈希跳转，避免切路由、开新页或滚动外层订单页面。 */
    onCitationClick(event) {
      const link = event.target?.closest?.("a.workflow-message-citation[data-workflow-sequence]");
      if (!link || !event.currentTarget.contains(link)) return;
      event.preventDefault(); event.stopPropagation();
      void this.jumpToMessage(link.dataset.workflowSequence);
    },
    clearCitation() {
      this.citationVersion++; this.citationContext = null; this.highlightedSequence = null;
      this.citationLoading = false; this.citationError = ""; this.citationOriginScroll = null;
    },
    /** 保存实时聊天窗口；远处引用单独分页浏览，不能把不连续历史合并进原消息游标。 */
    async jumpToMessage(value) {
      if (!this.active) return;
      const sequence = Number(value);
      if (!/^[1-9]\d*$/u.test(String(value)) || !Number.isSafeInteger(sequence) || sequence >= Number.MAX_SAFE_INTEGER || sequence > this.active.group.lastSequence) {
        this.citationError = this.$t("workflowChat.referenceUnavailable"); return;
      }
      const id = this.active.group.id, epoch = this.epoch, version = ++this.citationVersion;
      if (this.citationOriginScroll === null) this.citationOriginScroll = this.$refs.messages?.scrollTop || 0;
      this.citationLoading = true; this.citationError = ""; this.highlightedSequence = sequence;
      try {
        if (!this.displayedMessages.some(message => message.sequence === sequence)) {
          if (this.messages.some(message => message.sequence === sequence)) this.citationContext = null;
          else {
            const page = await api.getWorkflowChatMessages(id, { before: sequence + 1 });
            if (this.destroyed || epoch !== this.epoch || version !== this.citationVersion) return;
            if (!page.items.some(message => message.sequence === sequence)) throw new Error(this.$t("workflowChat.referenceUnavailable"));
            this.citationContext = { items: page.items, hasOlder: page.hasMore };
          }
        }
        await this.$nextTick();
        if (this.destroyed || epoch !== this.epoch || version !== this.citationVersion) return;
        const box = this.$refs.messages, target = box?.querySelector(`[data-workflow-sequence="${sequence}"]`);
        if (target) { box.scrollTop += target.getBoundingClientRect().top - box.getBoundingClientRect().top - Math.max(0, (box.clientHeight - target.offsetHeight) / 2); target.focus({ preventScroll: true }); }
      } catch (error) { this.handleCitationError(error, id, epoch, version); }
      finally { if (epoch === this.epoch && version === this.citationVersion) this.citationLoading = false; }
    },
    handleCitationError(error, id, epoch, version) {
      if (this.destroyed || epoch !== this.epoch || version !== this.citationVersion) return;
      if (["PARTICIPANT_FORBIDDEN", "GROUP_NOT_FOUND"].includes(error.errorCode) || [403, 404].includes(error.response?.status)) this.removeResource(id);
      else this.citationError = this.$t("workflowChat.referenceUnavailable");
    },
    /** 引用窗口最多保留200条；向两侧分页不覆盖实时消息、草稿和未读水位。 */
    async loadCitationPage(direction) {
      if (!this.citationContext || this.citationLoading || !this.active) return;
      const id = this.active.group.id, epoch = this.epoch, version = this.citationVersion;
      const items = this.citationContext.items, box = this.$refs.messages;
      const anchor = [...(box?.querySelectorAll("[data-workflow-sequence]") || [])].find(node => node.getBoundingClientRect().bottom > box.getBoundingClientRect().top);
      const anchorSequence = anchor?.dataset.workflowSequence, anchorTop = anchor?.getBoundingClientRect().top;
      this.citationLoading = true;
      try {
        const page = await api.getWorkflowChatMessages(id, direction === "older" ? { before: items[0].sequence } : { after: items[items.length - 1].sequence });
        if (this.destroyed || epoch !== this.epoch || version !== this.citationVersion) return;
        const merged = mergeWorkflowMessages(items, page.items);
        this.citationContext = { items: direction === "older" ? merged.slice(0, 200) : merged.slice(-200), hasOlder: direction === "older" ? page.hasMore : this.citationContext.hasOlder || merged.length > 200 };
        await this.$nextTick();
        if (this.destroyed || epoch !== this.epoch || version !== this.citationVersion) return;
        const retained = anchorSequence && box?.querySelector(`[data-workflow-sequence="${anchorSequence}"]`);
        if (retained) box.scrollTop += retained.getBoundingClientRect().top - anchorTop;
      } catch (error) { this.handleCitationError(error, id, epoch, version); }
      finally { if (epoch === this.epoch && version === this.citationVersion) this.citationLoading = false; }
    },
    async returnFromCitation() {
      const top = this.citationOriginScroll, epoch = this.epoch;
      this.clearCitation(); await this.$nextTick();
      if (!this.destroyed && epoch === this.epoch && this.$refs.messages && top !== null) this.$refs.messages.scrollTop = top;
    },
    toggleInfo() { this.infoOpen = !this.infoOpen; },
    replaceDraft(value) {
      this.mentionTokens = reconcileWorkflowMentions(this.draft, value, this.mentionTokens);
      this.draft = value;
    },
    mentioned(message) {
      return String(message.senderUserId) !== this.currentUserId && (message.mentions || []).some(m => m.kind === "ALL" || (m.kind === "USER" && m.userId === this.currentUserId));
    },
    privateChat(member) {
      if (String(member.userId) === this.currentUserId) return;
      this.$emit("private-chat", member);
    },
    closeMentions() {
      this.mentionOpen = false; this.mentionRange = null; this.mentionQuery = ""; this.mentionIndex = 0;
    },
    onMentionOutsideClick(event) {
      if (this.mentionOpen && !this.$refs.mentionPicker?.contains(event.target)) this.closeMentions();
    },
    updateMentionQuery({ value, caret, source }) {
      // 外部点击在捕获阶段收起后，同一次textarea点击不能重新展开；继续输入才触发。
      if (source === "click" && !this.mentionOpen) return;
      if (!this.visible || !this.active?.canSend) { this.closeMentions(); return; }
      const match = workflowMentionQuery(value, caret);
      this.mentionOpen = Boolean(match); this.mentionRange = match; this.mentionQuery = match?.query || ""; this.mentionIndex = 0;
    },
    insertMention(member) {
      const input = this.$refs.composer?.$refs.input;
      const result = insertWorkflowMention(this.draft, this.mentionRange, member.displayName, input?.selectionStart);
      const from = this.mentionRange?.from ?? input?.selectionStart ?? this.draft.length;
      this.replaceDraft(result.value);
      if (this.features.structuredMentions) {
        const mention = member.userId === "all" ? { kind: "ALL" } : member.userId === "agent" ? { kind: "AGENT", agentId: "group-chat" } : { kind: "USER", userId: String(member.userId) };
        this.mentionTokens.push({ from, to: result.caret - 1, text: `@${member.displayName}`, mention });
      }
      this.closeMentions();
      this.$nextTick(() => { input?.focus(); input?.setSelectionRange(result.caret, result.caret); });
    },
    mentionKeydown(event) {
      if (!this.mentionOpen || event.isComposing || event.keyCode === 229) return;
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); this.closeMentions(); }
      if (["ArrowDown", "ArrowUp"].includes(event.key) && this.mentionOptions.length) {
        event.preventDefault(); this.mentionIndex = (this.mentionIndex + (event.key === "ArrowDown" ? 1 : this.mentionOptions.length - 1)) % this.mentionOptions.length;
      }
      if (event.key === "Enter" && !event.shiftKey && this.mentionOptions[this.mentionIndex]) {
        event.preventDefault(); this.insertMention(this.mentionOptions[this.mentionIndex]);
      }
    },
    removeResource(id) {
      if (this.active?.group.id === id) this.clearCitation();
      this.groups = this.groups.filter(item => item.group.id !== id);
      this.$delete(this.drafts, id); this.$delete(this.readPositions, id);
      if (this.active?.group.id === id) {
        this.epoch++; this.active = null; this.preview = null; this.messages = []; this.draft = ""; this.error = "";
      }
      this.$emit("unavailable", id);
    },
    scopeKey: workflowScopeKey,
    scopeName(scope) {
      // 订单审批
      // 合同审批
      // 付款审批
      // 付款方式审批
      // 发票审批
      // 出库审批
      const labels = {
        // 订单审批
        0: this.$t("workflowChat.fullChain"),
        1: this.$t("workflowChat.orderApproval"),
        // 合同审批
        2: this.$t("workflowChat.contractApproval"),
        // 付款审批
        3: this.$t("workflowChat.paymentApproval"),
        // 付款方式审批
        4: this.$t("workflowChat.paymentMethodApproval"),
        // 发票审批
        5: this.$t("workflowChat.invoiceApproval"),
        // 出库审批
        6: this.$t("workflowChat.outboundApproval"),
      };
      // 流程讨论
      const name =
        // 流程讨论
        labels[scope?.reviewType] || this.$t("workflowChat.discussion");
      // {scope} · 第{number}期
      return Number(scope?.installmentNo) > 0
        ? // {scope} · 第{number}期
          this.$t("workflowChat.scopeInstallment", {
            scope: name,
            number: scope.installmentNo,
          })
        : name;
    },
    dateTime(value) {
      return value ? new Date(value).toLocaleString() : "";
    },
    // 发起人
    // 审核人
    // 抄送人
    // 、
    roleText(roles = []) {
      return (
        roles
          .map(
            (role) =>
              ({
                // 发起人
                INITIATOR: this.$t("contractRoles.initiator"),
                // 审核人
                REVIEWER: this.$t("devUpdates.reviewer"),
                // 抄送人
                CC: this.$t("contractReview.cc"),
              }[role] || role)
          )
          // 、
          .join(this.$t("workflowChat.roleSeparator"))
      );
    },
    // 讨论中
    // 结束后讨论期
    // 只读归档
    stateText(group) {
      return (
        {
          // 讨论中
          ACTIVE: this.$t("workflowChat.active"),
          // 结束后讨论期
          COOLING: this.$t("workflowChat.cooling"),
          // 只读归档
          ARCHIVED: this.$t("workflowChat.readonly"),
        }[group.state] || ""
      );
    },
    saveDraft() {
      this.captureViewPosition();
      if (this.active) { this.$set(this.drafts, this.active.group.id, this.draft); this.$set(this.draftMentions, this.active.group.id, this.mentionTokens); }
    },
    captureViewPosition() {
      const box = this.$refs?.messages;
      if (this.active && box?.clientHeight) this.$set(this.viewPositions, this.active.group.id, box.scrollTop);
    },
    restoreViewPosition() {
      const box = this.$refs?.messages, id = this.active?.group.id;
      if (box && Object.prototype.hasOwnProperty.call(this.viewPositions, id)) box.scrollTop = this.viewPositions[id];
    },
    async loadGroups(append = false) {
      if (this.conversationOnly) return;
      const version = ++this.listEpoch;
      const offset = append ? this.listOffset : 0;
      this.loading = true;
      try {
        const rows = await api.listWorkflowChats(this.archived, offset);
        if (this.destroyed || version !== this.listEpoch) return;
        this.groups = append ? [...this.groups, ...rows] : rows;
        this.listOffset = offset + 50;
        this.hasMoreGroups = rows.length === 50;
      } catch (error) {
        if (!this.destroyed && version === this.listEpoch)
          this.error = error.message;
      } finally {
        if (version === this.listEpoch) this.loading = false;
      }
    },
    async changeList() {
      await this.loadGroups();
    },
    async selectScope() {
      if (this.sending) return;
      const option = this.options.find(
        (entry) => this.scopeKey(entry.scope) === this.selectedScope
      );
      if (!option) return;
      this.saveDraft();
      this.clearCitation();
      this.mentionOpen = false; this.memberSearch = "";
      const version = ++this.epoch;
      this.active = null;
      this.preview = null;
      this.messages = [];
      this.error = "";
      try {
        const preview = await api.previewWorkflowChat(option.scope);
        if (this.destroyed || version !== this.epoch) return;
        if (preview.existingGroupId)
          await this.selectGroup(preview.existingGroupId);
        else this.preview = preview;
      } catch (error) {
        if (!this.destroyed && version === this.epoch)
          this.error = error.message;
      }
    },
    async selectGroup(id) {
      if (!id || (this.sending && !this.conversationOnly)) return;
      if (this.conversationOnly) this.sending = false;
      this.saveDraft();
      this.clearCitation();
      this.mentionOpen = false; this.memberSearch = "";
      const version = ++this.epoch;
      this.active = null; this.groupAgentReady = false;
      this.preview = null;
      this.messages = [];
      this.newMessages = false;
      this.error = "";
      this.loadingOlder = false;
      try {
        const [detail, page] = await Promise.all([
          api.getWorkflowChat(id),
          api.getWorkflowChatMessages(id),
        ]);
        if (this.destroyed || version !== this.epoch) return;
        this.active = detail;
        this.messages = page.items;
        this.hasOlder = page.hasMore;
        this.draft = this.drafts[id] || "";
        this.mentionTokens = this.draftMentions[id] || [];
        this.sendAttempt = null;
        if (Object.prototype.hasOwnProperty.call(this.viewPositions, id)) {
          await this.$nextTick(); this.restoreViewPosition();
        } else await this.scrollToLatest();
        if (this.features.agentMentions) {
          const ready = await api.workflowChatAgentAvailable(id);
          if (!this.destroyed && version === this.epoch) this.groupAgentReady = ready;
        }
      } catch (error) {
        if (!this.destroyed && version === this.epoch)
          this.error = error.message;
      }
    },
    async create() {
      if (this.sending || !this.preview?.canCreate || !this.opening.trim())
        return;
      const version = this.epoch;
      const text = this.opening.trim();
      const scope = this.preview.scope;
      if (
        !this.openingAttempt ||
        this.openingAttempt.openingMessage !== text ||
        this.scopeKey(this.openingAttempt.scope) !== this.scopeKey(scope)
      ) {
        this.openingAttempt = {
          scope,
          openingMessage: text,
          clientMessageId: workflowMessageId(),
        };
      }
      this.sending = true;
      this.error = "";
      let id;
      try {
        const detail = await api.openWorkflowChat(this.openingAttempt);
        if (!this.destroyed && version === this.epoch) id = detail.group.id;
      } catch (error) {
        if (!this.destroyed && version === this.epoch)
          this.error = error.message;
      } finally {
        this.sending = false;
      }
      if (id) {
        this.opening = "";
        this.openingAttempt = null;
        await this.selectGroup(id);
        await this.loadGroups();
      }
    },
    onKeydown(event) {
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.isComposing &&
        event.keyCode !== 229
      ) {
        event.preventDefault();
        this.send();
      }
    },
    async send() {
      if (this.sending || !this.active?.canSend || !this.draft.trim()) return;
      const id = this.active.group.id,
        version = this.epoch,
        text = this.draft.trim();
      const mentions = workflowMentionPayload(this.draft, this.mentionTokens);
      if (
        !this.sendAttempt ||
        this.sendAttempt.id !== id ||
        this.sendAttempt.content !== text || JSON.stringify(this.sendAttempt.mentions) !== JSON.stringify(mentions)
      )
        this.sendAttempt = {
          id,
          content: text,
          mentions,
          clientMessageId: workflowMessageId(),
        };
      this.sending = true;
      this.error = "";
      try {
        const message = await api.sendWorkflowChatMessage(id, {
          content: text,
          clientMessageId: this.sendAttempt.clientMessageId,
          ...(this.features.structuredMentions ? { mentions } : {}),
        });
        if (this.destroyed || version !== this.epoch) return;
        const last = this.messages[this.messages.length - 1]?.sequence || 0;
        this.draft = ""; this.mentionTokens = [];
        this.$set(this.draftMentions, id, []);
        this.$set(this.drafts, id, "");
        this.sendAttempt = null;
        if (message.sequence <= last + 1) {
          this.messages = mergeWorkflowMessages(this.messages, [message]);
          await this.scrollToLatest();
        } else await this.refresh();
      } catch (error) {
        if (!this.destroyed && version === this.epoch) {
          this.error = error.message;
          if (error.errorCode === "GROUP_ARCHIVED")
            this.active = { ...this.active, canSend: false };
        }
      } finally {
        this.sending = false;
      }
    },
    atBottom() {
      if (this.citationContext || this.highlightedSequence) return false;
      const box = this.$refs.messages;
      return box && box.scrollHeight - box.scrollTop - box.clientHeight < 40;
    },
    async markVisibleRead() {
      if (
        !this.visible || document.hidden || this.citationContext || this.highlightedSequence ||
        !this.active ||
        !this.atBottom() ||
        !this.messages.length
      )
        return;
      const id = this.active.group.id,
        version = this.epoch,
        sequence = this.messages[this.messages.length - 1].sequence;
      try {
        if ((this.readPositions[id] || 0) >= sequence) return;
        await api.readWorkflowChat(id, sequence);
        this.$set(this.readPositions, id, sequence);
        if (!this.destroyed && version === this.epoch) {
          this.newMessages = false;
          this.$emit("read", { groupId: id, sequence });
        }
      } catch (error) {
        if (!this.destroyed && version === this.epoch)
          this.error = error.message;
      }
    },
    onScroll() {
      if (this.atBottom()) this.markVisibleRead();
    },
    async scrollToLatest() {
      this.clearCitation();
      await this.$nextTick();
      const box = this.$refs.messages;
      if (box) box.scrollTop = box.scrollHeight;
      await this.markVisibleRead();
    },
    async loadOlder() {
      if (this.citationContext) return this.loadCitationPage("older");
      if (
        !this.active ||
        !this.hasOlder ||
        this.loadingOlder ||
        !this.messages.length
      )
        return;
      const id = this.active.group.id,
        version = this.epoch,
        box = this.$refs.messages;
      const height = box.scrollHeight,
        top = box.scrollTop;
      this.loadingOlder = true;
      try {
        const page = await api.getWorkflowChatMessages(id, {
          before: this.messages[0].sequence,
        });
        if (this.destroyed || version !== this.epoch) return;
        this.messages = mergeWorkflowMessages(this.messages, page.items);
        this.hasOlder = page.hasMore;
        await this.$nextTick();
        box.scrollTop = top + box.scrollHeight - height;
      } catch (error) {
        if (!this.destroyed && version === this.epoch)
          this.error = error.message;
      } finally {
        if (version === this.epoch) this.loadingOlder = false;
      }
    },
    async tick() {
      if (this.polling || this.destroyed) return;
      this.polling = true;
      try {
        if (!document.hidden) await this.refresh();
      } finally {
        this.polling = false;
      }
    },
    onVisibility() {
      if (!document.hidden) this.realtime?.refresh();
    },
    async refresh(depth = 0) {
      if (this.destroyed) return;
      await this.loadGroups();
      if (!this.active) return;
      const id = this.active.group.id,
        version = this.epoch,
        bottom = this.atBottom();
      try {
        const [detail, page] = await Promise.all([
          api.getWorkflowChat(id),
          api.getWorkflowChatMessages(id, {
            after: this.messages[this.messages.length - 1]?.sequence || 0,
          }),
        ]);
        if (this.destroyed || version !== this.epoch) return;
        this.active = detail;
        this.messages = mergeWorkflowMessages(this.messages, page.items);
        if (bottom && !page.hasMore && !this.highlightedSequence && !this.citationLoading) await this.scrollToLatest();
        else if (page.items.length) this.newMessages = true;
        // 消息积压时立即继续补差，不等待下一个推送或定时器。
        if (page.hasMore && page.items.length && version === this.epoch) {
          if (depth < 8) await this.refresh(depth + 1);
          else this.realtime?.refresh();
        }
      } catch (error) {
        if (!this.destroyed && version === this.epoch) {
          if (["PARTICIPANT_FORBIDDEN", "GROUP_NOT_FOUND"].includes(error.errorCode) || [403, 404].includes(error.response?.status)) this.removeResource(id);
          this.error = error.message;
        }
      }
    },
  },
};
</script>

<style scoped lang="less">
.workflow-chat {
  display: flex;
  height: min(70vh, 720px);
  min-height: 420px;
  color: #1f2937;
  font-size: 14px;
  &.embedded {
    flex: 1;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }
  .group-settings {
    max-height: 220px;
    overflow-y: auto;
    .manager { color: #667085; margin: 8px 0; }
    .member { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    button { flex-shrink: 0; min-height: 44px; border: 0; background: transparent; color: #245fc0; cursor: pointer; &:disabled { color: #98a2b3; cursor: default; } }
  }
  .citation-toolbar {
    display: flex; align-items: center; gap: 8px; flex: 0 0 auto; padding: 8px 12px; background: #eef4ff; color: #245fc0;
    span { flex: 1; min-width: 0; }
    button { flex-shrink: 0; min-height: 44px; padding: 0 8px; border: 0; background: transparent; color: #245fc0; cursor: pointer; &:disabled { color: #98a2b3; } }
  }
  .message.is-cited .body { outline: 2px solid #4286f5; outline-offset: 2px; background: #eef4ff; }
  .body ::v-deep a.workflow-message-citation { color: #245fc0; text-decoration: underline; text-underline-offset: 3px; &:focus-visible { outline: 2px solid #4286f5; outline-offset: 2px; } }
  .mention-picker {
    flex: 0 0 auto;
    max-height: 180px;
    overflow-y: auto;
    background: #fff;
    border-top: 1px solid #dde3ee;
    button { display: block; width: 100%; min-height: 44px; padding: 8px 16px; text-align: left; border: 0; background: #fff; &.selected, &:hover { background: #eef4ff; } }
  }
  .sidebar {
    flex: 0 0 260px;
    border-right: 1px solid #dde3ee;
    display: flex;
    flex-direction: column;
  }
  .scope-picker {
    padding: 12px;
    label {
      display: block;
      margin-bottom: 8px;
    }
    .el-select {
      width: 100%;
    }
  }
  .list-controls {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
  }
  .group-list {
    overflow-y: auto;
  }
  .group-row {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 12px 16px;
    gap: 8px;
    text-align: left;
    border: 0;
    background: #fff;
    cursor: pointer;
    color: inherit;
    &.selected,
    &:hover {
      background: #eef4ff;
    }
    &:focus-visible {
      outline: 2px solid #245fc0;
      outline-offset: -2px;
    }
    strong,
    span {
      overflow-wrap: anywhere;
    }
    small {
      color: #667085;
    }
  }
  .mention-label { color: #246de0; font-size: 12px; }
  .discussion {
    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  &.compact-header {
    .messages { padding-top: 56px; }
    .citation-toolbar { margin-top: 56px; }
    .citation-toolbar + .messages { padding-top: 12px; }
  }
  &.is-mobile {
    &.compact-header {
      .messages { padding: 12px; }
      .citation-toolbar { margin-top: 0; }
    }
    .heading.info-popover { top: 0; left: 8px; right: 8px; max-height: 80%; }
    .heading { padding: 12px; overflow-wrap: normal; details { max-height: none; overflow: visible; } summary { display: flex; align-items: center; min-height: 44px; white-space: nowrap; } ::v-deep .el-input__inner { height: 44px; font-size: 16px; } }
    .group-info-back { display: inline-flex; align-items: center; justify-content: center; align-self: flex-start; width: 44px; height: 44px; padding: 0; border: 0; background: transparent; color: #475467; }
    .member { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin: 4px 0; span { min-width: 0; overflow-wrap: anywhere; } button { flex: 0 0 auto; min-height: 44px; padding: 8px 12px; border: 1px solid #dde3ee; border-radius: 8px; background: #fff; color: #246de0; white-space: nowrap; } }
    .citation-toolbar { flex: 0 0 auto; overflow-x: auto; flex-wrap: nowrap; button { min-height: 44px; white-space: nowrap; } }
    .message .body { max-width: 100%; box-sizing: border-box; padding: 10px 12px; }
    .mention-picker { max-height: min(160px, 35%); button { min-height: 44px; white-space: nowrap; } }
    .readonly { flex: 0 0 auto; margin: 0; padding: 12px 12px calc(12px + env(safe-area-inset-bottom, 0px)); }
  }
  .heading.info-popover { position: absolute; z-index: 8; top: 52px; left: 12px; right: 12px; max-height: min(65%, 360px); border: 1px solid #dde3ee; border-radius: 12px; background: #fff; box-shadow: 0 8px 24px rgba(15,23,42,.1); transform-origin: top left; clip-path: inset(0 round 12px); }
  .group-info-enter-active, .group-info-leave-active { transition: clip-path 240ms cubic-bezier(.2,.8,.2,1), transform 240ms cubic-bezier(.2,.8,.2,1), opacity 240ms cubic-bezier(.2,.8,.2,1); }
  .heading.group-info-enter, .heading.group-info-leave-to { opacity: 0; transform: translateY(-8px) scale(.98); clip-path: inset(0 calc(100% - 44px) calc(100% - 44px) 0 round 12px); }
  @media (prefers-reduced-motion: reduce) { .group-info-enter-active, .group-info-leave-active { transition: none; } }
  .heading {
    flex: 0 1 auto;
    min-height: 48px;
    max-height: 40%;
    box-sizing: border-box;
    overflow: auto;
    overflow-wrap: anywhere;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    border-bottom: 1px solid #dde3ee;
    span,
    small {
      color: #667085;
    }
    summary {
      cursor: pointer;
    }
    details {
      max-height: 140px;
      overflow: auto;
    }
  }
  .member {
    display: inline-block;
    margin: 8px 12px 0 0;
  }
  .messages {
    flex: 1;
    min-width: 0;
    overflow-x: hidden;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    background: #f8fafc;
  }
  .message {
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    &.mine {
      align-items: flex-end;
      .body {
        background: #e8f0ff;
      }
    }
    .author {
      color: #667085;
      font-size: 12px;
      margin-bottom: 8px;
    }
    .body {
      padding: 12px 16px;
      background: #fff;
      border: 1px solid #dde3ee;
      border-radius: var(--chat-radius, 12px);
      max-width: 90%;
      overflow-wrap: anywhere;
      line-height: 1.6;
    }
  }
  /deep/ .body {
    p {
      margin: 0 0 8px;
      &:last-child {
        margin: 0;
      }
    }
    pre {
      white-space: pre-wrap;
    }
    ul,
    ol {
      padding-left: 20px;
    }
    table {
      display: block;
      overflow: auto;
      max-width: 100%;
    }
    a {
      color: #245fc0;
    }
  }
  .composer {
    min-width: 0;
    display: flex;
    align-items: flex-end;
    gap: 12px;
    padding: 12px;
    border-top: 1px solid #dde3ee;
    .el-button {
      min-height: 44px;
      background: #245fc0;
      border-color: #245fc0;
    }
  }
  .opening {
    padding: 24px;
    overflow-y: auto;
    line-height: 1.7;
    .el-button {
      margin-top: 16px;
      min-height: 44px;
    }
  }
  .empty,
  .readonly {
    padding: 24px;
    color: #667085;
    text-align: center;
  }
  .error {
    padding: 8px 16px;
    background: #fff4ed;
    color: #9a3412;
  }
  .new-messages {
    position: absolute;
    bottom: 148px;
    right: 24px;
  }
  /deep/ .el-button,
  /deep/ .el-radio-button__inner {
    min-height: 44px;
    height: auto;
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 20px;
  }
  @media (max-width: 640px) {
    flex-direction: column;
    height: 74vh;
    min-height: 400px;
    .sidebar {
      flex: 0 0 auto;
      max-height: 220px;
      border-right: 0;
      border-bottom: 1px solid #dde3ee;
    }
    .group-list {
      display: flex;
      overflow-x: auto;
    }
    .group-row {
      min-width: 200px;
      min-height: 44px;
    }
    .discussion {
      min-height: 240px;
      overflow: hidden;
    }
    .heading {
      padding: 12px;
    }
    .composer {
      gap: 8px;
      padding-bottom: calc(12px + env(safe-area-inset-bottom));
    }
  }
}
</style>
